import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import {
  CheckCircle2,
  FlaskConical,
  Loader2,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import { getAgencies, runDemoAgencySetup } from '../../services/api';
import type {
  AgencyResponse,
  DemoAgencySetupResponse,
  DemoAgencySetupStepId,
  DemoAgencySetupStepResult,
  DemoAgencySetupStepStatus,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { useShallow } from 'zustand/react/shallow';
import {
  DEFAULT_DEMO_FF_TEMPLATE_AGENCY,
  DEFAULT_DEMO_TEAM_MEMBER_EMAILS,
} from './demoAgencySetupDefaults';
import {
  inferDemoAgencySetupStageLabel,
  isDemoAgencySetupPanelEnabled,
} from './demoAgencySetupUi';

/** Matches backend `DEMO_AGENCY_SETUP_STEP_ORDER` (not the OpenAPI enum dump order). */
const DEMO_SETUP_STEP_ORDER: readonly DemoAgencySetupStepId[] = [
  'validate',
  'wipe_test_data',
  'configure_agency',
  'migrate_kinnser_clinics',
  'seed_intakes',
  'seed_completed_charts',
  'seed_qa_hotbox',
  'seed_billing_demo',
  'seed_reporting_demo',
  'seed_rogerdoc_coding',
] as const;

const STEP_LABELS: Record<DemoAgencySetupStepId, string> = {
  validate: 'Validate agency',
  wipe_test_data: 'Wipe test data',
  configure_agency: 'Configure agency',
  migrate_kinnser_clinics: 'Migrate Kinnser clinics',
  seed_intakes: 'Seed intakes',
  seed_billing_demo: 'Seed billing / NOA demo',
  seed_completed_charts: 'Seed completed charts',
  seed_qa_hotbox: 'Seed QA hotbox',
  seed_reporting_demo: 'Seed reporting demo',
  seed_rogerdoc_coding: 'Seed RogerDoc coding',
};

type OverallRunKind = 'success' | 'failed' | 'incomplete';

/** UI-only status when the POST dies without a step payload. */
type DisplayStepStatus = DemoAgencySetupStepStatus | 'unknown';

type DisplayStep = Omit<DemoAgencySetupStepResult, 'status'> & {
  status: DisplayStepStatus;
};

function formatAgencySelectLabel(agency: AgencyResponse): string {
  const systemName = agency.system_name.trim();
  const writtenName = (agency.written_name ?? '').trim();
  if (!writtenName || writtenName.toLowerCase() === systemName.toLowerCase()) {
    return systemName;
  }
  return `${writtenName} (${systemName})`;
}

const DEMO_TEAM_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidDemoTeamEmail(email: string): boolean {
  const trimmed = email.trim();
  if (!trimmed) return true;
  return DEMO_TEAM_EMAIL_PATTERN.test(trimmed);
}

function deriveOverallRunKind(steps: DisplayStep[]): OverallRunKind {
  if (steps.some(step => step.status === 'failed')) {
    return 'failed';
  }
  if (steps.some(step => step.status === 'not_implemented')) {
    return 'incomplete';
  }
  return 'success';
}

function statusBadgeVariant(
  status: DisplayStepStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'succeeded':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'skipped':
      return 'secondary';
    case 'not_implemented':
    case 'unknown':
      return 'outline';
    case 'pending':
    default:
      return 'secondary';
  }
}

function pendingSteps(
  includeCoding: boolean,
  hasDemoEmails: boolean
): DisplayStep[] {
  return DEMO_SETUP_STEP_ORDER.map(id => {
    if (id === 'seed_rogerdoc_coding' && !includeCoding) {
      return {
        id,
        status: 'skipped' as const,
        message: 'Will skip because include coding is off.',
        error: null,
      };
    }
    if (
      (id === 'seed_completed_charts' || id === 'seed_qa_hotbox') &&
      !hasDemoEmails
    ) {
      return {
        id,
        status: 'skipped' as const,
        message: 'Will skip because no demo team member emails were provided.',
        error: null,
      };
    }
    return {
      id,
      status: 'pending' as const,
      message: null,
      error: null,
    };
  });
}

/** After a transport/timeout failure we never learn which steps finished. */
function markPendingStepsUnknown(steps: DisplayStep[]): DisplayStep[] {
  return steps.map(step => {
    if (step.status !== 'pending') {
      return step;
    }
    return {
      ...step,
      status: 'unknown' as const,
      message:
        'Outcome unknown — the request ended before step results arrived. This step may already have run.',
    };
  });
}

export const DemoAgencySetupPanel: React.FC = () => {
  const stageLabel = useMemo(() => inferDemoAgencySetupStageLabel(), []);

  if (!isDemoAgencySetupPanelEnabled()) {
    return null;
  }

  return <DemoAgencySetupPanelContent stageLabel={stageLabel} />;
};

const DemoAgencySetupPanelContent: React.FC<{ stageLabel: string }> = ({
  stageLabel,
}) => {
  const { showError } = useStore(
    useShallow(state => ({
      showError: state.showError,
    }))
  );

  const [agencies, setAgencies] = useState<AgencyResponse[]>([]);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [agencySystemName, setAgencySystemName] = useState('');
  const [templateAgencySystemName, setTemplateAgencySystemName] = useState(
    DEFAULT_DEMO_FF_TEMPLATE_AGENCY
  );
  const [teamMemberEmails, setTeamMemberEmails] = useState<string[]>([
    ...DEFAULT_DEMO_TEAM_MEMBER_EMAILS,
  ]);
  const [includeCoding, setIncludeCoding] = useState(true);
  const [useDefaultFeatureFlags, setUseDefaultFeatureFlags] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const [result, setResult] = useState<DemoAgencySetupResponse | null>(null);
  const [displaySteps, setDisplaySteps] = useState<DisplayStep[] | null>(null);

  const loadAgencies = useCallback(async () => {
    setLoadingAgencies(true);
    try {
      const res = await getAgencies({
        query: { limit: 1000 },
        throwOnError: true,
      });
      const all = [...(res.data ?? [])];
      all.sort((a, b) =>
        (a.written_name || a.system_name).localeCompare(
          b.written_name || b.system_name
        )
      );
      setAgencies(all);
    } catch (error) {
      showError(
        'Failed to Load Agencies',
        getUserFacingApiMessage(error, 'Unable to load agencies.')
      );
    } finally {
      setLoadingAgencies(false);
    }
  }, [showError]);

  useEffect(() => {
    void loadAgencies();
  }, [loadAgencies]);

  const kinnserAgencies = useMemo(
    () => agencies.filter(agency => agency.is_kinnser_enabled === true),
    [agencies]
  );

  const toAgencyOption = (agency: AgencyResponse) => ({
    value: agency.system_name,
    label: formatAgencySelectLabel(agency),
  });

  const agencyOptions = useMemo(
    () => kinnserAgencies.map(toAgencyOption),
    [kinnserAgencies]
  );

  const displayTemplateAgency = useDefaultFeatureFlags
    ? DEFAULT_DEMO_FF_TEMPLATE_AGENCY
    : templateAgencySystemName;

  const templateAgencyOptions = useMemo(() => {
    const options = agencies.map(toAgencyOption);
    const selected = displayTemplateAgency.trim();
    if (selected && !options.some(option => option.value === selected)) {
      options.unshift({
        value: selected,
        label: selected,
      });
    }
    return options;
  }, [agencies, displayTemplateAgency]);

  const effectiveTemplateAgency = useDefaultFeatureFlags
    ? DEFAULT_DEMO_FF_TEMPLATE_AGENCY
    : templateAgencySystemName.trim();

  const normalizedEmails = useMemo(
    () =>
      teamMemberEmails.map(email => email.trim().toLowerCase()).filter(Boolean),
    [teamMemberEmails]
  );
  const hasInvalidTeamEmails = teamMemberEmails.some(
    email => !isValidDemoTeamEmail(email)
  );
  const selectedAgency = useMemo(
    () =>
      kinnserAgencies.find(agency => agency.system_name === agencySystemName),
    [kinnserAgencies, agencySystemName]
  );
  const selectedAgencyLabel = selectedAgency
    ? formatAgencySelectLabel(selectedAgency)
    : agencySystemName;
  const canSubmit =
    Boolean(agencySystemName) &&
    Boolean(effectiveTemplateAgency) &&
    !hasInvalidTeamEmails &&
    !running &&
    !loadingAgencies;

  const overallKind =
    displaySteps && !running ? deriveOverallRunKind(displaySteps) : null;

  const runSetup = async () => {
    if (!agencySystemName) return;

    setConfirmOpen(false);
    setRunning(true);
    setRequestFailed(false);
    setResult(null);
    setDisplaySteps(pendingSteps(includeCoding, normalizedEmails.length > 0));

    try {
      const res = await runDemoAgencySetup({
        body: {
          agency_system_name: agencySystemName,
          include_coding: includeCoding,
          demo_ff_template_agency: effectiveTemplateAgency,
          demo_team_member_emails: normalizedEmails,
        },
        // Orchestrator is a single blocking POST; avoid client abort mid-run.
        timeout: 0,
        throwOnError: true,
      });
      const data = res.data as DemoAgencySetupResponse;
      setResult(data);
      setDisplaySteps(data.steps);
      setRequestFailed(false);
    } catch (error) {
      // Keep the checklist, but mark still-pending steps unknown — wipe/configure
      // may already have committed even though we never got a step payload.
      setRequestFailed(true);
      setDisplaySteps(prev =>
        markPendingStepsUnknown(
          prev ?? pendingSteps(includeCoding, normalizedEmails.length > 0)
        )
      );
      showError(
        'Demo setup failed',
        getUserFacingApiMessage(
          error,
          'Unable to run demo agency setup. Check environment and try again.'
        )
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className='flex flex-row items-start justify-between gap-4 space-y-0'>
          <div className='space-y-1'>
            <CardTitle className='flex items-center gap-2'>
              <FlaskConical className='h-5 w-5' />
              Setup demo environment
            </CardTitle>
            <CardDescription className='text-xs'>
              Run the GTM demo agency orchestrator for a Kinnser-synced agency.
              Non-production only.
            </CardDescription>
          </div>
          <Badge variant='outline' className='shrink-0 font-mono text-xs'>
            {stageLabel.toUpperCase()}
          </Badge>
        </CardHeader>

        <CardContent className='space-y-4'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2 md:items-start'>
            <div className='space-y-4 min-w-0'>
              {loadingAgencies ? (
                <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Loading agencies…
                </div>
              ) : (
                <>
                  <label className='flex flex-col gap-1'>
                    <span className='text-sm font-medium'>Target agency</span>
                    <VirtualizedFormSelect
                      key={`demo-agency-${agencySystemName || 'none'}-${kinnserAgencies.length}`}
                      placeholder='Search Kinnser agencies…'
                      options={agencyOptions}
                      sortOptions
                      selectSize='sm'
                      isClearable={false}
                      defaultValue={agencySystemName}
                      onChange={(e: { target: { value: string } }) =>
                        setAgencySystemName(e.target.value)
                      }
                      menuPortalTarget={null}
                      menuPosition='absolute'
                      disabled={running}
                    />
                  </label>

                  {!loadingAgencies && kinnserAgencies.length === 0 && (
                    <p className='text-sm text-muted-foreground'>
                      No Kinnser-enabled agencies found in this environment.
                    </p>
                  )}
                </>
              )}

              <label className='flex items-start gap-2 text-sm cursor-pointer'>
                <Checkbox
                  checked={includeCoding}
                  onCheckedChange={(checked: boolean | 'indeterminate') =>
                    setIncludeCoding(checked === true)
                  }
                  disabled={running}
                  className='mt-0.5'
                  aria-label='Include coding'
                />
                <span>
                  Include coding
                  <span className='block text-xs text-muted-foreground'>
                    Runs the RogerDoc coding seed step when enabled.
                  </span>
                </span>
              </label>

              <label className='flex items-start gap-2 text-sm cursor-pointer'>
                <Checkbox
                  checked={useDefaultFeatureFlags}
                  onCheckedChange={(checked: boolean | 'indeterminate') =>
                    setUseDefaultFeatureFlags(checked === true)
                  }
                  disabled={running}
                  className='mt-0.5'
                  aria-label='Use default feature flags'
                />
                <span>
                  Use default feature flags
                  <span className='block text-xs text-muted-foreground'>
                    Copies from {DEFAULT_DEMO_FF_TEMPLATE_AGENCY} by default.
                  </span>
                </span>
              </label>

              {!loadingAgencies && (
                <div className='pl-6 flex flex-col gap-1'>
                  <span
                    className={
                      useDefaultFeatureFlags || running
                        ? 'text-xs font-medium text-gray-300'
                        : 'text-xs font-medium text-gray-700'
                    }
                  >
                    Feature-flag template agency
                  </span>
                  <VirtualizedFormSelect
                    placeholder='Search agencies…'
                    options={templateAgencyOptions}
                    sortOptions
                    selectSize='sm'
                    isClearable={false}
                    value={displayTemplateAgency}
                    onChange={(e: { target: { value: string } }) =>
                      setTemplateAgencySystemName(e.target.value)
                    }
                    menuPortalTarget={null}
                    menuPosition='absolute'
                    disabled={running || useDefaultFeatureFlags}
                  />
                </div>
              )}
            </div>

            <div className='space-y-2 min-w-0'>
              <div className='text-sm font-medium'>
                Demo user emails
                <span className='block text-xs font-normal text-muted-foreground'>
                  Invited with hardcoded demo roles. Empty list skips invites
                  and chart/QA seeds.
                </span>
              </div>
              <div className='space-y-2'>
                {teamMemberEmails.map((email, index) => (
                  <div key={index} className='flex items-center gap-2'>
                    <Input
                      type='email'
                      value={email}
                      placeholder='name@rogerhealthcare.com'
                      disabled={running}
                      onChange={(e: { target: { value: string } }) => {
                        const next = [...teamMemberEmails];
                        next[index] = e.target.value;
                        setTeamMemberEmails(next);
                      }}
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      disabled={running}
                      aria-label={`Remove email ${index + 1}`}
                      onClick={() =>
                        setTeamMemberEmails(prev =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <Trash2 className='h-4 w-4' />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={running}
                onClick={() => setTeamMemberEmails(prev => [...prev, ''])}
              >
                <Plus className='h-4 w-4 mr-1' />
                Add email
              </Button>
              {hasInvalidTeamEmails && (
                <p className='text-sm text-red-600'>
                  Enter a valid email address for each row, or remove the row.
                </p>
              )}
            </div>
          </div>

          <div className='flex flex-col items-end gap-2 pt-1'>
            <Button
              variant='destructive'
              disabled={!canSubmit}
              onClick={() => setConfirmOpen(true)}
            >
              {running ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                  Running setup…
                </>
              ) : (
                'Setup demo environment'
              )}
            </Button>

            {running && (
              <p className='text-sm text-muted-foreground text-right max-w-md'>
                This may take up to a minute. Keep this tab open until the step
                checklist finishes.
              </p>
            )}
          </div>

          {displaySteps && (
            <div className='space-y-3 pt-2 border-t'>
              {requestFailed && !running && (
                <Alert className='border-red-200 bg-red-50'>
                  <AlertDescription className='text-red-900 flex gap-2 items-start text-sm'>
                    <XCircle className='h-4 w-4 text-red-600 mt-0.5 shrink-0' />
                    <span>
                      The setup request failed before a full step response
                      arrived. Pending steps are marked unknown — they may
                      already have run. Avoid re-running until you confirm the
                      agency state.
                    </span>
                  </AlertDescription>
                </Alert>
              )}
              {overallKind === 'success' && result && (
                <Alert className='border-green-200 bg-green-50'>
                  <AlertDescription className='text-green-900 flex gap-2 items-center text-sm'>
                    <CheckCircle2 className='h-4 w-4 text-green-600 shrink-0' />
                    Demo setup completed for{' '}
                    <span className='font-medium'>
                      {result.agency_system_name}
                    </span>
                    .
                  </AlertDescription>
                </Alert>
              )}
              {overallKind === 'failed' && (
                <Alert className='border-red-200 bg-red-50'>
                  <AlertDescription className='text-red-900 flex gap-2 items-start text-sm'>
                    <XCircle className='h-4 w-4 text-red-600 mt-0.5 shrink-0' />
                    <span>
                      Demo setup finished with one or more failed steps
                      {result ? ` for ${result.agency_system_name}` : ''}. Later
                      steps may still have run — review the checklist before
                      demos.
                    </span>
                  </AlertDescription>
                </Alert>
              )}
              {overallKind === 'incomplete' && (
                <Alert className='border-amber-200 bg-amber-50'>
                  <AlertDescription className='text-amber-900 text-sm'>
                    Run finished without hard failures, but one or more steps
                    are still <strong>not implemented</strong> on this branch.
                    Treat the environment as partially seeded.
                  </AlertDescription>
                </Alert>
              )}

              <ul className='space-y-2'>
                {displaySteps.map(step => (
                  <li
                    key={step.id}
                    className='rounded-md border px-3 py-2 text-sm space-y-1'
                  >
                    <div className='flex flex-wrap items-center gap-2'>
                      <span className='font-medium'>
                        {STEP_LABELS[step.id] ?? step.id}
                      </span>
                      <Badge
                        variant={statusBadgeVariant(step.status)}
                        className='font-normal capitalize'
                      >
                        {String(step.status).replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {step.message && (
                      <p className='text-muted-foreground'>{step.message}</p>
                    )}
                    {step.error && <p className='text-red-700'>{step.error}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open: boolean) => {
          if (!running) setConfirmOpen(open);
        }}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Confirm demo environment setup</DialogTitle>
          </DialogHeader>
          <div className='space-y-2 text-sm text-muted-foreground py-1'>
            <p>
              This will wipe existing test patient / demo seed data for{' '}
              <span className='font-medium text-foreground'>
                {selectedAgencyLabel}
              </span>{' '}
              and re-run the orchestrator steps
              {includeCoding ? ', including RogerDoc coding' : ''}.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setConfirmOpen(false)}
              disabled={running}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={() => void runSetup()}
              disabled={running}
            >
              Run setup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DemoAgencySetupPanel;
